import { settings } from "@/settings";
import {
  Analysis,
  AnalysisRating,
  businessDataUtils,
  Offer,
  Question,
  Task,
  TaskExecuteAction,
  TaskState,
} from "@/types";
import {
  deepClone,
  deleteAttributesRecursively,
  headers,
  removeAttributesRecursively,
  UON,
} from "@/utils";
import { useApi } from "@/requests/useApi";
import { api } from "@/auth/api";
import { executeTask } from "@/store/services/task/workflow";
import store from "@/store";
import { saveOfferFromTask } from "../taskService";
import i18n from "@/i18n";
import Notify from 'quasar/src/plugins/Notify.js';;
import { updateFile } from "@/store/services/task/actions/validateDocumentService";
import moment from "moment/moment";
import { logger } from "@/utils/logger";

const odm_analysis = `odm-analysis`;
const odm_decision = `odm-decision`;
const systemUid = `${settings.api_url}/${odm_analysis}/api/1/${odm_analysis}`;
//const baseUrl = `${settings.api_url}/${odm_analysis}/api/1/${odm_analysis}`
const odm_supportingdocument = "odm-supportingdocument";
const supportingDocumentBaseUrl = `${settings.api_url}/${odm_supportingdocument}/api/1/${odm_supportingdocument}`;

function normalizeRatingType(type: unknown): string {
  return String(type ?? "").trim().toLowerCase();
}

function getTaskAnalysisResourceUid(): string | undefined {
  const raw = store.state.taskModule?.selectedTask?.variables?._decisionAnalysisProcessId;
  if (typeof raw === "string" && raw) {
    return raw;
  }
  if (raw && typeof raw === "object" && (raw as { value?: string }).value) {
    return (raw as { value: string }).value;
  }
  return (
    store.state.analysisModule?.analysis?.resourceUid ||
    store.state.taskModule?.analysisModule?.analysis?.resourceUid
  );
}

function findCurrentAnalysisRating(analysisRatings: any[] | undefined, type: unknown) {
  const key = normalizeRatingType(type);
  return analysisRatings?.find((rating) => normalizeRatingType(rating?.type) === key);
}

function mapAnalysisRatingToEvaluateFormat(rating: any) {
  return {
    resourceUid: rating?.resourceUid ?? null,
    objectType: rating?.objectType ?? "odm.analysis.rating",
    systemUid: rating?.systemUid ?? "odm-analysis",
    businessData: rating?.businessData ?? null,
    daaq: rating?.daaq ?? null,
    calculatedValue: rating?.calculatedValue ?? null,
    updatedValue: rating?.updatedValue ?? null,
    type: rating?.type,
    decision: rating?.decision ?? "null",
    comment: rating?.comment ?? "null",
    validity: rating?.validity ?? null,
  };
}

function resolveAnalysisStateForTask() {
  const taskAnalysisId = getTaskAnalysisResourceUid();
  const root = store.state.analysisModule;
  const nested = store.state.taskModule?.analysisModule;

  const analysis =
    (taskAnalysisId && root?.analysis?.resourceUid === taskAnalysisId && root.analysis) ||
    (taskAnalysisId && nested?.analysis?.resourceUid === taskAnalysisId && nested.analysis) ||
    (root?.analysis?.resourceUid ? root.analysis : nested?.analysis);

  const nestedInfo = nested?.analysisDataInfo;
  const rootInfo = root?.analysisDataInfo;
  const analysisDataInfo =
    (nestedInfo?.evaluateRatings?.length ? deepClone(nestedInfo) : null) ||
    (rootInfo?.evaluateRatings?.length ? deepClone(rootInfo) : null) ||
    (nestedInfo ? deepClone(nestedInfo) : rootInfo ? deepClone(rootInfo) : null);

  return { analysis, analysisDataInfo };
}

export function buildAnalysisDataInfoPayload() {
  const { analysis, analysisDataInfo } = resolveAnalysisStateForTask();
  const analysisRatings = (analysis?.analysisRatings ?? []).filter(
    (rating) => !(rating as AnalysisRating & { isOfferAdditionalData?: boolean }).isOfferAdditionalData
  );

  const base = analysisDataInfo ?? {
    evaluateRatios: analysis?.ratios ?? [],
    evaluateRatings: [],
    evaluateScore: analysis?.score ?? analysis?.analysisScore ?? null,
  };

  let evaluateRatings = Array.isArray(base.evaluateRatings) ? [...base.evaluateRatings] : [];

  if (evaluateRatings.length > 0 && analysisRatings.length > 0) {
    evaluateRatings = evaluateRatings.map((rating: any) => {
      const current = findCurrentAnalysisRating(analysisRatings, rating?.type);
      if (!current) {
        return rating;
      }
      return {
        ...rating,
        updatedValue:
          current.updatedValue !== undefined && current.updatedValue !== null && current.updatedValue !== ""
            ? current.updatedValue
            : rating.updatedValue ?? null,
        calculatedValue: current.calculatedValue ?? rating.calculatedValue,
        decision: current.decision ?? rating.decision,
        comment: current.comment ?? rating.comment,
      };
    });
  } else if (!evaluateRatings.length && analysisRatings.length > 0) {
    evaluateRatings = analysisRatings.map(mapAnalysisRatingToEvaluateFormat);
  }

  return {
    ...base,
    evaluateRatings,
    evaluateScore: base.evaluateScore ?? analysis?.score ?? analysis?.analysisScore ?? null,
  };
}

/** Aligns Camunda payload with current UI ratings before task execute / controls. */
export function syncAnalysisDataInfoForExecute() {
  const payload = buildAnalysisDataInfoPayload();
  if (store.state.analysisModule) {
    store.state.analysisModule.analysisDataInfo = payload;
  }
  if (store.state.taskModule?.analysisModule) {
    store.state.taskModule.analysisModule.analysisDataInfo = deepClone(payload);
  }
  return payload;
}

export async function executeAnalysisAction(
  taskState: TaskState,
  userId: string,
  offer: Offer,
  task: Task
) {
  const { anyDocsQualified } = anyQualifiedDocuments();

  const analysisDataInfo = syncAnalysisDataInfoForExecute();
  
  if (anyDocsQualified) {
    Notify.create({
      actions: [{ icon: "close", color: "white" }],
      message: i18n.global.t("main.dialog.errorQualifiedDocs"),
      color: "warning",
      timeout: 5000,
    });
  }

  let taskExecuteAction: TaskExecuteAction = { taskId: "" };

  const customerParty = offer.associatedParties?.find(
    (el: any) =>
      el.associatedParty?.role.resourceUid === "CUSTOMER" &&
      el.associatedParty.third.type.id.includes("Organization")
  );

  const balanceSheets = customerParty
    ? customerParty?.associatedParty?.third?.organization?.balanceSheets
    : null;
  const chiffreAffaireDetails = balanceSheets
    ? balanceSheets[0]?.detail?.filter(
        (item: any) => item.nature ? item.nature.id.includes("affaire") :null
      )
    : null;

  const parsePartyData = (p: string) => JSON.parse(JSON.parse(p));
  const stringifyPartyData = (o: any) => JSON.stringify(JSON.stringify(o));
  const toMoney = (v: any) =>
      v && typeof v === "object" && "amount" in v
          ? v
          : { amount: Number(v ?? 0), currency: "EUR" };

  const normalizeExposure = (e: any) => ({
    objectType: e.objectType ?? "odm.party.partyfinancialexposure",
    systemUid: e.systemUid ?? "odm-party",
    resourceUid: e.resourceUid ?? null,
    businessData: {
      ...(e.businessData ?? {}),
      MT_EC_GLOBAL_Q_TTC:
          (toMoney(e.grossOutstanding)?.amount ?? Number(e.grossOutstanding ?? 0)),
    },
    daaq: e.daaq ?? "/",
    grossOutstanding: toMoney(e.grossOutstanding),
    netOutstanding: toMoney(e.netOutstanding),
    residualValue: toMoney(e.residualValue),
    flagFinancialPool: e.flagFinancialPool ?? false,
    flagRiskPool: e.flagRiskPool ?? false,
    shareFinancialPool: e.shareFinancialPool ?? null,
    shareRiskPool: e.shareRiskPool ?? null,
    effectiveDate: e.effectiveDate ?? null,
    contractReference: e.contractReference ?? null,
    contractValidity:
        e.contractValidity && Object.keys(e.contractValidity).length === 0
            ? null
            : e.contractValidity ?? null,
    outstandingType: e.outstandingType
        ? {
          objectType: "odm.party.outstandingtype",
          systemUid: "odm-party",
          resourceUid:
              e.outstandingType.resourceUid ??
              e.outstandingType.value ??
              e.outstandingType.type?.value ??
              null,
          businessData: null,
          daaq: null,
        }
        : null,
    financialInstitution: e.financialInstitution
        ? {
          objectType: "odm.party.financialinstitution",
          systemUid: "odm-party",
          resourceUid:
              e.financialInstitution.resourceUid ??
              e.financialInstitution.value ??
              e.financialInstitution.type?.value ??
              null,
          businessData: null,
          daaq: null,
        }
        : null,
  });

  const exposureFinancialExposureKey = (e: any) =>
      String(e?.contractReference ?? e?.resourceUid ?? "");


  //store.state.taskModule.offer = await saveOfferFromTask(offer);
  let customerName;

  const partyInfoBase = (store.state.taskModule.offer?.associatedParties || []).map(
      (offerAssociatedParty: any) => {
        const { role_code } = offerAssociatedParty;
        const { third } = offerAssociatedParty.associatedParty;
        const type = third.type.id;
        const body = type.includes("Organization")
            ? { ...third.organization }
            : { ...third.person };
           if(offerAssociatedParty?.associatedParty?.role?.resourceUid === "CUSTOMER"){
             if(type.includes("Organization")){
              customerName = third.organization.name;
             }else {
              const name = third.person.firstName + ' ' + (third.person.lastName ?  third.person.lastName : third.person.familyName);
              customerName = name;
             }
            }
        removeAttributesRecursively(body);
        deleteAttributesRecursively(body, ["isDisabled", "config"]);
        const partyData = {
          type,
          role: offerAssociatedParty.associatedParty.role_code
              ? offerAssociatedParty.associatedParty.role_code
              : role_code || "CLIENT",
          ...body,
        };
        return {
          partyData: stringifyPartyData(partyData),
        };
      }
  );

  const rawPartyInfoFromCamunda = taskState.selectedTask?.variables?.partyInfo;

  const previousPartyInfo =
      typeof rawPartyInfoFromCamunda === "string"
          ? JSON.parse(rawPartyInfoFromCamunda)
          : Array.isArray(rawPartyInfoFromCamunda)
              ? rawPartyInfoFromCamunda
              : [];

  const baseByResourceUid = new Map<string, any>();
  const baseByRole = new Map<string, any>();

  partyInfoBase.forEach((item: any) => {
    const current = item.partyData;
    if (current?.resourceUid)
      baseByResourceUid.set(String(current.resourceUid), current);
    if (current?.role) baseByRole.set(String(current.role), current);
  });

  const partyInfoMerged = partyInfoBase.map((p: any) => {
    const partyFromCamunda = parsePartyData(p.partyData);

    const baseObj =
        (partyFromCamunda?.resourceUid
            ? baseByResourceUid.get(String(partyFromCamunda.resourceUid))
            : undefined) ??
        (partyFromCamunda?.role ? baseByRole.get(String(partyFromCamunda.role)) : undefined);

    if (!baseObj) {
      return { partyData: stringifyPartyData(partyFromCamunda) };
    }

    //Adding balanceSheets
    if (Array.isArray(baseObj.balanceSheets) && baseObj.balanceSheets.length > 0) {
      partyFromCamunda.balanceSheets = baseObj.balanceSheets;
    }

    //Adding IDs
    if (Array.isArray(baseObj.ids) && baseObj.ids.length > 0) {
      partyFromCamunda.ids = baseObj.ids;
    }

    //Adding CodeApe
    if (baseObj.activitySector) {
      partyFromCamunda.codeAPE = baseObj.activitySector;
    }

    //Adding accounts
    if (Array.isArray(baseObj.accounts) && baseObj.accounts.length > 0) {
      const toIsoDate = (s: any) => {
        if (!s) return s;
        const str = String(s).trim();
        const m = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (!m) return str;
        const [, dd, mm, yyyy] = m;
        return `${yyyy}-${mm}-${dd}`;
      };

      partyFromCamunda.accounts = baseObj.accounts.map((acc: any) => ({
        ...acc,
        bankAccount: acc?.bankAccount
            ? {
              ...acc.bankAccount,
              status: acc.bankAccount.status?.objectType
                  ? acc.bankAccount.status
                  : {
                    objectType: "odm.party.bankaccountstatus",
                    systemUid: "odm-party",
                    resourceUid: acc.bankAccount.status?.id ?? acc.bankAccount.status?.resourceUid ?? null,
                    rawData: null,
                  },
              type: acc.bankAccount.type?.objectType
                  ? acc.bankAccount.type
                  : {
                    objectType: "odm.party.bankaccounttype",
                    systemUid: "odm-party",
                    resourceUid: acc.bankAccount.type?.id ?? acc.bankAccount.type?.resourceUid ?? null,
                    rawData: null,
                  },
              validity: acc.bankAccount.validity
                  ? {
                    from: acc.bankAccount.validity.from === "" ||
                    acc.bankAccount.validity.from === null ||
                    acc.bankAccount.validity.from === undefined
                        ? null
                        : toIsoDate(acc.bankAccount.validity.from),
                    until:
                        acc.bankAccount.validity.until === "" ||
                        acc.bankAccount.validity.until === null ||
                        acc.bankAccount.validity.until === undefined
                            ? null
                            : toIsoDate(acc.bankAccount.validity.until),
                  }
                  : acc.bankAccount.validity,
            }
            : acc?.bankAccount,
      }));
    }


    //Adding Facilities.address
    if (
        Array.isArray(baseObj.facilities) &&
        baseObj.facilities.some(
            (f: any) =>
                f?.address &&
                f.address.line1 !== null &&
                f.address.line1 !== undefined &&
                String(f.address.line1).trim() !== ""
        )
    ) {
      partyFromCamunda.facilities = baseObj.facilities;
    }

    //Adding FinancialExposures
    const financialExposuresFromCamunda = Array.isArray(partyFromCamunda.financialExposures)
        ? partyFromCamunda.financialExposures
        : [];
    const financialExposureUpdated = Array.isArray(baseObj.financialExposures)
        ? baseObj.financialExposures
        : [];
    const keys = new Set(financialExposuresFromCamunda.map(exposureFinancialExposureKey));
    financialExposureUpdated.forEach((e: any) => {
      const k = exposureFinancialExposureKey(e);
      if (k && !keys.has(k)) financialExposuresFromCamunda.push(normalizeExposure(e));
    });
    partyFromCamunda.financialExposures = financialExposuresFromCamunda;

    return { partyData: stringifyPartyData(partyFromCamunda) };
  });

  if (chiffreAffaireDetails) {
    taskExecuteAction = {
      taskId: task.taskId || "",

      variables: {
        chiffreAffaireDetails: {
          value: JSON.stringify(chiffreAffaireDetails),
          type: "Json",
        },
        analysisDataInfo: {
          value: JSON.stringify(analysisDataInfo),
          type: "Json",
        },
        _partyInfo: {
          value: JSON.stringify(partyInfoBase),
          type: "Json",
        },
        analysisAnswersOptions: {
          value: JSON.stringify(store.state.analysisModule?.selectedAnswersOptions),
          type: "Json",
        },
        customerName: {
          value: customerName ,
          type: "String",
        }
      },
    };

  } else {
    taskExecuteAction = {
      taskId: task.taskId || "",
      variables: {
        analysisDataInfo: {
          value: JSON.stringify(analysisDataInfo),
          type: "Json",
        },
        analysisAnswersOptions: {
          value: JSON.stringify(store.state.analysisModule?.selectedAnswersOptions),
          type: "Json",
        },
        
        _partyInfo: {
          value: JSON.stringify(partyInfoBase),
          type: "Json",
        },
        customerName: {
          value: customerName,
          type: "String",
        }
      },
    };
  }
  taskExecuteAction.variables.active_user = {value : JSON.stringify(store.getters['authModule/getActiveUserInfo']), type: "Json"}
  taskExecuteAction.variables.signatureMethod = {value : store.state.demandModule.signatureMethod, type: "String"}
  taskExecuteAction.variables.analysisMakerId = {value : store.state.authModule?.user?.userId, type: "String"}
  store.state.taskModule.offer = await saveOfferFromTask(offer);

  await validDocAnalysesProcess();

  return executeTask(taskExecuteAction);
}



export function anyQualifiedDocuments() {
  const qualified = i18n.global.t(
    `task.uploadDocument.headers.supportingDocumentStatus.RECEIVED`
  );

  const gatheringProcess = store.state.taskModule.gatheringProcess;
  const anyDocsQualified = gatheringProcess
    .flatMap((gp: any) => gp.entityTargets)
    .flatMap((targetEntity: any) => targetEntity.documents)
    .some((doc: any) => doc.status === qualified || doc.status === "RECEIVED");

  return { anyDocsQualified };
}

export function validDocuments() {
  const valid = i18n.global.t(
    `task.uploadDocument.headers.supportingDocumentStatus.VALID`
  );
  const qualified = i18n.global.t(
    `task.uploadDocument.headers.supportingDocumentStatus.RECEIVED`
  );

  const gatheringProcess = store.state.taskModule.gatheringProcess;
  const allDocsValid = !gatheringProcess
    .flatMap((gp: any) => gp.entityTargets)
    .flatMap((targetEntity: any) => targetEntity.documents)
    .some(
      (doc: any) =>
        doc.status !== valid &&
        doc.status !== "VALID" &&
        doc.status !== qualified &&
        doc.status !== "RECEIVED"
    );

  return { allDocsValid };
}

export async function validDocAnalysesProcess() {
  try {
    const gatheringProcess = store.state.taskModule.gatheringProcess;
    const sentToValidProcess: string[] = [];
    for (const gp of gatheringProcess) {
      for (const targetEntity of gp.entityTargets) {
        for (const doc of targetEntity.documents) {
          if(doc.document.comment || doc.document.expirationDate){
            const updateFileRequestPayload = {
              comment: doc.document.comment,
              expirationDate: doc.document.expirationDate ? moment(doc.document.expirationDate).format("yyyy-MM-DD") : null,
              forceUpdate: true,
              fileReportReference: {},
              businessData: {
                statusNetheos: doc.document.netheosStatus,
                addedBy: doc.addedBy
              }
            };
            await updateFile(doc.processResourceUid, doc.receivedFileUid, updateFileRequestPayload)
          }

          if (sentToValidProcess.indexOf(doc.processResourceUid) < 0) {
            await validProcess(doc.processResourceUid);
            sentToValidProcess.push(doc.processResourceUid);
            logger.info("Documents Validated successfully")
/*            Notify.create({
              timeout: 5000,
              actions: [{ icon: "close", color: "white" }],
              message: i18n.global.t("main.dialog.validation.success"),
              color: "positive",
            });*/
          }
        }
      }
    }
  } catch (e) {
    logger.error('Error in executeDecisionAction', e as Error);
    return Promise.reject(e);
  }
}

async function validProcess(processId: string) {
  const validationRequestPayload = {
    resourceUid: processId,
    objectType: "odm.supportingdocument.gatheringprocess",
    systemUid: "odm-supportingdocument",
    status: {
      objectType: "odm.supportingdocument.processstatus",
      systemUid: "odm-supportingdocument",
      resourceUid: "VALID",
    },
  };
  return api().put(
    `${supportingDocumentBaseUrl}/process/${processId}/valid/`,
    validationRequestPayload
  );
}

function setAnalysis(analysisCloned: Analysis, offerId?: string) {
  // const array = analysisCloned.opinionQualifications[0].opinionQualifications.length;
  if (
    analysisCloned.opinionQualifications &&
    analysisCloned.opinionQualifications.length > 0 &&
    analysisCloned.opinionQualifications[0].opinionQualifications.length > 0
  ) {
    analysisCloned = {
      ...analysisCloned,

      analysisEntity: {
        resourceUid: analysisCloned.analysisEntity?.resourceUid,
        objectType: "odm.decision.decisionprocess",
        systemUid: "odm-decision",
      },
      reference: analysisCloned.reference,
      // status.internalCode:analysisCloned.status.internalCode==="IN_PROGRESS",
      opinionQualifications:
        analysisCloned.opinionQualifications[0].opinionQualifications,
    };
  } else {
    analysisCloned = {
      ...analysisCloned,

      owner: {
        objectType: "odm.user.leaseforgeuser",
        systemUid: "odm-user",
        resourceUid: store.state.authModule?.user.userId, //this.$store.state.authModule?.user.userId,
      },

      analysisEntity: {
        resourceUid: analysisCloned.analysisEntity?.resourceUid,
        objectType: "odm.decision.decisionprocess",
        systemUid: "odm-decision",
      },
      reference: analysisCloned.reference,
      // status.internalCode:analysisCloned.status.internalCode==="IN_PROGRESS",
      opinionQualifications: [],
    };
  }

  return analysisCloned;
}
export async function takeOpinion(analysis: Analysis, offerId?: string) {
  const clone = setAnalysis(deepClone(analysis), offerId);
  const { error } = await useApi(UON.SystemUid.odmAnalysis, "process").upsert(
    clone
  );
  if (error) {
    throw error;
  }
}

export async function getAnalysis(resourceUID: string) {
  return new Promise<any>(async (resolve, reject) => {
    await api()
      .get(`${systemUid}/process/${resourceUID}/`)
      .then((res) => resolve(res.data))
      .catch((e) => reject(e));
  });
}
export async function getOpinion(resourceUid: string) {
  return new Promise<any>(async (resolve, reject) => {
    await api()
      .get(`${systemUid}/opinion/${resourceUid}/`)
      .then((res) => resolve(res.data))
      .catch((e) => reject(e));
  });
}

export async function updateAnalysis(analysis: Analysis, offerId?: string) {
  const clone = setAnalysis(deepClone(analysis), offerId);

  const { response, error } = await useApi(
    UON.SystemUid.odmAnalysis,
    "process"
  ).upsert(clone);
  if (error) {
    throw error;
  }
  return response;
}

export async function getAnalysisAlgorithmeData(
  resourceUID: string,
  payload: any,
  targetApi: string
) {
  const res = await api().post(
    `${systemUid}/analysis/${resourceUID}/${targetApi}/`,
    payload
  );
  return res.data;
}

export async function getScoreAnalysis(resourceUID: string) {
  return new Promise<any>(async (resolve, reject) => {
    await api()
      .get(`${systemUid}/analysisscore/${resourceUID}/`)
      .then((res) => resolve(res.data))
      .catch((e) => reject(e));
  });
}

export const getQuestionsByProcessId = (process_id: string) =>
  new Promise((resolve, reject) => {
    return api()
      .get(settings.api_query_url, {
        headers: headers({
          qc: JSON.stringify({
            queryId: "weleaf-questionnaire-questions",
            offset: 0,
            limit: 10000,
            parameters: { process_id: process_id },
          }),
        }),
      })
      .then((response) => {
        resolve(response.data);
      })
      .catch((err) => {
        reject(err);
      });
  });

export const getRatiosByProcessId = (process_id: string,type:Boolean) =>
  new Promise((resolve, reject) => {
    return api()
      .get(settings.api_query_url, {
        headers: headers({
          qc: JSON.stringify({
            queryId: "weleaf-ratio-category",
            offset: 0,
            limit: 10000,
            parameters: { process_id: process_id },
          }),
        }),
      })
      .then((response) => {
        let ratios;
        if(type){
          ratios = response.data.filter((item:any) => item.category_code !== "RESIDUEL");
        }
        else ratios = response.data;
        
        resolve(ratios);
      })
      .catch((err) => {
        reject(err);
      });
  });

export async function UpdateQuestionnaireGathering(
  questions: Question[],
  gatheringItem: any
) {
  const clone = {
    ...gatheringItem,
    questions: questions,
  };

  const { response, error } = await useApi(
    UON.SystemUid.odmSupportingDocument,
    "questionnairegathering"
  ).upsert(clone);
  if (error) {
    throw error;
  }
  return response;
}

export async function getAnalysisEvaluatedReserves(payload: any) {
  const systemUid = `${settings.api_url}/${odm_decision}/api/1/${odm_decision}`;
  const analysisData = JSON.parse(payload.data);
  const request = {
    // "applicationName": payload.task.variables.applicationName.value,
    applicationName: "Origination",
    context: "Financing",
    partyInfo: analysisData.partyInfo,
    quoteInfo: JSON.stringify(analysisData.quoteInfo),
  };

  return new Promise<any>(async (resolve, reject) => {
    await api()
      .post(`${systemUid}/decision/selection/reserves/`, request)
      .then((res) => resolve(res.data))
      .catch((e) => reject(e));
  });
}

export async function getAssociatedAnalysisProcess(resourceUID: string) {
  const systemUid = `${settings.api_url}/${odm_decision}/api/1/${odm_decision}`;

  return new Promise<any>(async (resolve, reject) => {
    await api()
      .get(`${systemUid}/decisionprocess/${resourceUID}/`)
      .then((res) => resolve(res.data))
      .catch((e) => reject(e));
  });
}

export async function updateAssociatedAnalysisProcess(payload: any) {
  const { response, error } = await useApi(
    UON.SystemUid.odmDecision,
    "associatedanalysisprocess"
  ).upsert(payload);
  if (error) {
    throw error;
  }
  return response;
}

export const decisionMock = async () => {
  return await [
    {
      decisionId: 1,
      decision: "Offre Acceptée",
      date: "19/12/2023 14:28",
      decideur: "Vendeur 1",
      profile: "Vendeur Partenaire",
      avis: [],
    },
    {
      decisionId: 2,
      decision: "Décision en cours",
      date: "-",
      decideur: "-",
      profile: "-",
      avis: [
        {
          avisId: 1,
          dateAvis: "19/12/2023 14:28",
          avis: "Favorable",
          referenceAnalyse: "ert1234",
          decideur: "Analyste_Junior",
          commentaire: "Avis favorable",
          reserves: [
            { id: 1, reserve: "Hypothèque", commentaire: "teamwill" },
            { id: 2, reserve: "Caution avec quotité %", commentaire: "weleaf" },
          ],
        },
        {
          avisId: 2,
          dateAvis: "19/12/2023 14:28",
          avis: "Favorable",
          referenceAnalyse: "uy659",
          decideur: "Analyste_Senior",
          commentaire: "Avis favorable",
          reserves: [{ id: 1, reserve: "Hypothèque", commentaire: "efficom" }],
        },
      ],
    },
  ];
};
